import { z } from 'zod';
import { pageQuerySchema } from './marketplace.js';
import { userRoleSchema } from './schemas.js';
/**
 * Messaging, with the guardian in the room.
 *
 * The safeguarding rule is the design: an adult may not open a thread with a
 * teen out of nowhere. A conversation only exists in the context of a job the
 * teen has already applied to, bid on, or been assigned — and when the teen is
 * a minor, their verified guardian is added to that thread automatically as a
 * silent reader. [Teen steps 11-12, Parent steps 8/11/13, Job Poster steps 15/21]
 */
/* -------------------------------------------------------------------------- */
/* Enums mirrored from the database                                           */
/* -------------------------------------------------------------------------- */
export const conversationTypeSchema = z.enum([
    'TEEN_PROVIDER',
    'PARENT_PROVIDER',
    'PARENT_TEEN',
    'THREE_WAY',
    'PARENT_PARENT',
]);
/**
 * FULL can post; MONITOR_ONLY can only read.
 *
 * A guardian watching their minor's thread is MONITOR_ONLY: they see everything
 * and say nothing, which is oversight rather than participation. Upgrading them
 * to FULL is what turns a two-way thread into the three-way one the job poster
 * journey asks for.
 */
export const participantVisibilitySchema = z.enum(['FULL', 'MONITOR_ONLY']);
/* -------------------------------------------------------------------------- */
/* Reading                                                                    */
/* -------------------------------------------------------------------------- */
export const conversationParticipantSchema = z.object({
    /** The participant's user publicId. */
    id: z.string(),
    name: z.string(),
    role: userRoleSchema,
    visibility: participantVisibilitySchema,
    /** True for the account making the request. */
    isMe: z.boolean(),
});
export const messageSchema = z.object({
    id: z.string(),
    conversationId: z.string(),
    senderId: z.string(),
    senderName: z.string(),
    senderRole: userRoleSchema,
    content: z.string(),
    sentAt: z.string(),
    /** Lets the UI align the bubble without comparing ids in a component. */
    isMine: z.boolean(),
    /**
     * True when `content` was withheld because this reader is a guardian who has
     * set their oversight to metadata only. The message still appears — who sent
     * it and when is the part they kept — so the thread does not silently shrink.
     * [Parent step 11]
     */
    redacted: z.boolean(),
});
export const conversationSchema = z.object({
    id: z.string(),
    type: conversationTypeSchema,
    /** Every thread hangs off a job — there is no context-free chat. */
    job: z
        .object({
        id: z.string(),
        title: z.string(),
    })
        .nullable(),
    participants: z.array(conversationParticipantSchema),
    lastMessage: messageSchema.nullable(),
    unreadCount: z.number().int().min(0),
    /** False for a guardian who is only watching. */
    canSend: z.boolean(),
    createdAt: z.string(),
});
/* -------------------------------------------------------------------------- */
/* Writing                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Opening a thread names the job and the other person, never a conversation id —
 * the server decides whether one already exists, so tapping "Message" twice
 * cannot produce two threads about the same thing.
 */
export const startConversationInputSchema = z.object({
    /**
     * The job the thread is about.
     *
     * Required for anything involving a teen, because a job is what gives an
     * adult standing to contact them at all. Two guardians who have accepted
     * each other need no job — the connection is the relationship — and a
     * guardian talking to their own teen does not either.
     */
    jobId: z.string().uuid().optional(),
    /**
     * Who to talk to, as the publicId the caller already has: a teen, parent or
     * job poster profile id, or the account id itself. The server resolves all
     * four to the same person, so a screen never has to fetch a second id just
     * to open a thread.
     */
    withPersonId: z.string().uuid(),
});
export const sendMessageInputSchema = z.object({
    content: z.string().trim().min(1, 'Write a message first').max(4000),
});
export const conversationListQuerySchema = pageQuerySchema.extend({
    jobId: z.string().uuid().optional(),
});
export const messageListQuerySchema = pageQuerySchema;
/** Total unread across every thread — the messages badge. */
export const unreadMessageCountSchema = z.object({
    unread: z.number().int().min(0),
});
//# sourceMappingURL=messaging.js.map